import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import MaterialReactTable from "material-react-table";
import { Box, Button, ButtonBase, IconButton, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

import { EsgDeleteIcon, EsgEditIcon, EsgLeftArrow, EsgRECPendingIcon, EsgRECUploadedIcon, PlusIcon } from "assets";
import { deleteRecData, getRecDataBySite } from "api/rec";
import { useGetAllowedValues, useSiteById, useNotify } from "hooks";
import { COLORS, DeleteDialog, Label14Medium, Loading, useDialogControls } from "components";
import { extractErrorMessage } from "utils/helpers";

import { RecModal } from "./components";
import { IRecDetails } from "./types";
import { REC_DETAIL_COLUMN } from "./consts";

dayjs.extend(timezone);

export const RecUpload: React.FunctionComponent = () => {
  // ---- Hooks ----
  const navigate = useNavigate();
  const notify = useNotify();
  const { collectorId } = useParams();
  const deleteDialogControls = useDialogControls();

  // ---- Const-----
  const query = new URLSearchParams(useLocation().search);
  const siteId = query.get("s");

  // ---- API"s ----
  const { data: site } = useSiteById(siteId as any, true);
  const { data: recData, mutateAsync: getRecDocument, isLoading } = useMutation(getRecDataBySite);
  const { data: allowedValues } = useGetAllowedValues();
  const { mutateAsync: deleteRec } = useMutation(deleteRecData);

  // ---- State ----
  const [isRecModalOpen, setRecModalOpen] = useState(false);
  const [selectedRECData, setSelectedRECData] = useState<IRecDetails | null>(null);

  // ---- Page Functions ----
  const handleCloseRecModal = () => {
    setRecModalOpen(false);
    setSelectedRECData(null);
    getRecDocument(collectorId)
  }

  const handleEditCollector = (rowData: any) => {
    setRecModalOpen(true)
    setSelectedRECData(rowData);
  };

  const handleDeleteREC = () => {
    if (selectedRECData?.recId) {
      deleteRec({ recId: selectedRECData?.recId, collectorId: collectorId as string }, {
        onSuccess: () => {
          getRecDocument(collectorId);
          deleteDialogControls.close();
          notify.success("Deleted Successfully")
        },
        onError: (e: any) => notify.error(e?.response?.data ?? "Some error has happen while deleting Utility Provider Renewable Energy Rate!"),
      }).catch((e) => notify.error(extractErrorMessage(e) ?? "Some error has happen while deleting Utility Provider Renewable Energy Rate!"));
    } else {
      notify.error("Some error has happen while deleting Utility Provider Renewable Energy Rate!")
    }
    setSelectedRECData(null);
    deleteDialogControls.close();
  };

  // ---- Use Effects ----
  useEffect(() => {
    getRecDocument(collectorId)
  }, [collectorId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <Loading />;

  return (
    <Box sx={{ margin: 3 }}>
      <ButtonBase sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        "&:active": {
          backgroundColor: "rgba(0, 0, 0, 0.12)",
        },
      }}
        onClick={() => navigate(-1)}
      >
        <EsgLeftArrow />
        <Label14Medium>Back to Utility Providers</Label14Medium>
      </ButtonBase>
      <Button
        sx={{ my: 2, borderRadius: "8px" }}
        variant="contained"
        onClick={() => setRecModalOpen(true)}
        startIcon={<PlusIcon />}
        disabled={!site?.enabled}
      >
        Create New Utility Provider Renewable Energy Rate
      </Button>
      <br />
      <Box>
        {recData && <MaterialReactTable
          muiTableContainerProps={{
            sx: {
              "& tbody tr:first-of-type td > p": {
                display: "flex",
                width: "100%",
                justifyContent: "center",
              },
              border: "1px solid #D9D9D9", borderRadius: "10px",
              minWidth: "100%",
              width: "100%",
              maxWidth: "100%",
              "@media (min-width: 600px)": {
                width: "100%",
              },
              "@media (min-width: 960px)": {
                width: "1200px",
              },
              "@media (min-width: 1280px)": {
                width: "1200px",
              },
              "@media (min-width: 1440px)": {
                width: "1200px",
              },
              "@media (min-width: 1680px)": {
                width: "1400px",
              },
              "@media (min-width: 2400px)": {
                width: "100%",
              },
            }
          }}
          muiTablePaperProps={{
            sx: {
              borderRadius: "10px",
              boxShadow: "none"
            }
          }}
          muiTopToolbarProps={{
            sx: {
              display: "none"
            }
          }}
          muiBottomToolbarProps={{
            sx: {
              display: "none"
            }
          }}
          muiTableHeadCellProps={{
            sx: {
              lineHeight: "30px",
              alignItems: "center",
              color: COLORS.Romance,
              fontWeight: "600",
              fontSize: "14px",
              fontStyle: "Inter",
              "& .MuiBadge-root": {
                display: "none",
              },
            }
          }}
          muiTableHeadRowProps={{
            sx: {
              height: "40px",
              backgroundColor: "#F8FAFC",
              borderBottomColor: "#F8FAFC",
              textAlign: "center",
              alignItems: "center"

            }
          }}
          muiTableBodyRowProps={{
            sx: {
              height: "40px",
              backgroundColor: "white",
            }
          }}
          columns={REC_DETAIL_COLUMN}
          data={recData?.data as any}
          enablePagination={false}
          enableDensityToggle={false}
          enableRowActions
          initialState={{
            density: "compact",
          }}
          renderRowActions={({ row }) => {
            const recId = row?.original?.recId
            return (
              <Box sx={{
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRight: "1px solid rgba(224,224,224,1)",
                paddingRight: "5px"
              }}>
                {row?.original?.isRecsUploaded ? <Tooltip arrow placement="top" title="Utility Provider Renewable Energy Rate is Uploaded">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={() => navigate(`/recDetail/${recId}`)}>
                    <EsgRECUploadedIcon />
                  </IconButton>
                </Tooltip> : <Tooltip arrow placement="top" title="Add Utility Provider Renewable Energy Rate">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={() => navigate(`/recDetail/${recId}?s=${siteId}`)}>
                    <EsgRECPendingIcon />
                  </IconButton>
                </Tooltip>}
                <Tooltip arrow placement="top" title="Edit">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    disabled={!site?.enabled}
                    onClick={() => handleEditCollector(row?.original)}>
                    <EsgEditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip arrow placement="top" title="Delete">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setSelectedRECData(row?.original);
                      deleteDialogControls.open()
                    }}
                  >
                    <EsgDeleteIcon color="red" />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          }}
        />}
      </Box>
      <br />
      {(isRecModalOpen || isRecModalOpen) &&
        <RecModal
          isOpen={isRecModalOpen || isRecModalOpen}
          editREC={selectedRECData}
          onClose={handleCloseRecModal}
          columns={REC_DETAIL_COLUMN}
          allowedValues={allowedValues}
          site={siteId as any}
        />}
      {deleteDialogControls.isOpen && <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={handleDeleteREC}
        target="Utility Provider Renewable Energy Rate"
        title="Delete Utility Provider Renewable Energy Rate?"
      />}
    </Box>
  );
};